import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ActionIcon, Badge, Box, Group, SimpleGrid, Tooltip } from '@mantine/core';
import { IconGripVertical, IconTrash } from '@tabler/icons-react';
import type { CardView } from '@creativitas/shared';
import { AssetImage } from '@/components/ui/AssetImage';

/**
 * Card ordering inside a deck (§18.5, §63).
 *
 * Drag-and-drop is used only where it is the natural gesture; every card also
 * carries its position number so the order is readable without dragging.
 */

function SortableCard({
  card,
  index,
  onDelete,
}: {
  card: CardView;
  index: number;
  onDelete: (cardId: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: card.id,
  });

  return (
    <Box
      ref={setNodeRef}
      pos="relative"
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 2 : 1,
      }}
    >
      <AssetImage
        src={card.frontImage}
        alt={card.title ?? `Карта ${index + 1}`}
        ratio="7 / 12"
        fallbackLabel="Нет изображения"
      />

      <Badge
        size="xs"
        variant="filled"
        color="teal"
        pos="absolute"
        style={{ left: 6, top: 6, pointerEvents: 'none' }}
      >
        {index + 1}
      </Badge>

      <Group gap={4} pos="absolute" style={{ right: 6, top: 6 }}>
        <Tooltip label="Перетащить">
          <ActionIcon
            size="sm"
            variant="filled"
            color="dark"
            aria-label={`Переместить карту ${index + 1}`}
            style={{ cursor: 'grab' }}
            {...attributes}
            {...listeners}
          >
            <IconGripVertical size={13} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Удалить карту">
          <ActionIcon
            size="sm"
            variant="filled"
            color="red"
            aria-label={`Удалить карту ${index + 1}`}
            onClick={() => onDelete(card.id)}
          >
            <IconTrash size={13} />
          </ActionIcon>
        </Tooltip>
      </Group>
    </Box>
  );
}

type CardSorterProps = {
  cards: CardView[];
  onReorder: (cardIds: string[]) => void;
  onDelete: (cardId: string) => void;
};

export function CardSorter({ cards, onReorder, onDelete }: CardSorterProps) {
  const sensors = useSensors(
    // A small distance keeps a click on the delete button from starting a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = cards.findIndex((card) => card.id === active.id);
    const newIndex = cards.findIndex((card) => card.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    onReorder(arrayMove(cards, oldIndex, newIndex).map((card) => card.id));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToParentElement]}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={cards.map((card) => card.id)} strategy={rectSortingStrategy}>
        <SimpleGrid cols={{ base: 3, sm: 5, lg: 8 }} spacing="sm">
          {cards.map((card, index) => (
            <SortableCard key={card.id} card={card} index={index} onDelete={onDelete} />
          ))}
        </SimpleGrid>
      </SortableContext>
    </DndContext>
  );
}
